<template>
  <div class="input-group" :class="`${inputChanged}`">
    <i v-if="icon && iconType === 'icon' && (iconPosition === 'right' ? !loading : true)" :class="iconClass" class="input-group__icon"></i>
    <a
      type="submit"
      v-if="iconType === 'button' && icon && (iconPosition === 'right' ? !loading : true)"
      class="input-group__icon"
      :class="iconClass"
      @click.prevent="disabled ? () => {} : $emit('iconClick')"
    ></a>

    <div
      class="input-group__input"
      :class="error ? 'input-group__input--error has-error' : ''"
      @click="onFocus"
      v-if="immutable"
    >{{value ? value : placeholder}}</div>

    <input
      v-else
      :type="typeName"
      :placeholder="placeholder"
      :disabled="disabled"
      :min="min"
      :max='max'
      :autocomplete="autocomplete"
      class="input-group__input"
      :class="error ? 'input-group__input--error has-error' : ''"
      v-model="inputValue"
      @focus.prevent.stop="onFocus"
      @blur="$emit('blur')"
      @change="emitChange"
      @keydown.down="$emit('keydown:down')"
      @keydown.up="$emit('keydown:up')"
      @keydown.enter="$emit('keydown:enter')"
      ref="vInput"
      v-scroll-into-view
    >
    <label
      class="input-group__label"
      v-if="label || placeholder">

      {{label ? label : placeholder}}

    </label>
    <div class="input-group__spinner" v-if="loading">
      <spinner width="3" height="3"></spinner>
    </div>
    <div class="input-group__error" v-if="error">{{error}}</div>
  </div>
</template>
<script>
import Spinner from '@/ui/elements/Spinner'

import emitInputValue from '@/mixins/emitInputValue'

import scrollIntoView from '@/directives/scrollIntoView'
import mask from '@/directives/mask'

import parsePhoneNumber from 'libphonenumber-js'

export default {
  name: 'v-input',
  mixins: [emitInputValue],
  directives: {
    scrollIntoView,
    mask
  },
  components: {
    Spinner
  },
  data () {
    return {
      localValue: this.value
    }
  },
  props: {
    autocomplete: {
      type: String,
      default: 'off'
    },

    loading: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: 'placeholder'
    },
    /**
     * @model
     */
    value: {
      type: [String, Number],
      default: null
    },
    label: {
      type: String,
      default: null
    },
    /**
     * A class name of the icon from icon font, ex. "icon-search"
     */
    icon: {
      type: String,
      default: null
    },
    typeName: {
      type: String,
      default: 'text'
    },
    min: {
      type: [String, Number],
      default: null
    },
    max: {
      type: [String, Number],
      default: null
    },
    immutable: {
      type: Boolean,
      default: false
    },
    /**
     * @values left, right
     */
    iconPosition: {
      type: String,
      default: 'right'
    },
    /**
     * @values icon, button
     */
    iconType: {
      type: String,
      default: 'icon'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    },
    maxLength: {
      type: Number,
      default: null
    }
  },
  methods: {
    emitChange (e) {
      this.$emit('blur')
      this.$emit('change', e)
    },
    onFocus () {
      this.$emit('focus')
    }
  },
  watch: {
    value (val) {
      if (this.maxLength !== null && val !== null) {
        this.localValue = val.substring(0, this.maxLength)
      } else {
        this.localValue = val
      }
    }
  },
  computed: {
    inputValue: {
      get () {
        if (this.typeName === 'tel') {
          const phoneNumber = parsePhoneNumber(this.value ?? '')
          if (!phoneNumber) return this.value
          return phoneNumber.formatInternational()
        }
        if (this.typeName === 'card' && typeof this.value === 'string') {
          const sep = (xs, s) => xs.length ? [xs.slice(0, s), ...sep(xs.slice(s), s)] : []
          const arr = this.value.split('')
          let out = ''
          const separated = sep(arr, 4)

          separated.forEach((item, i) => {
            let joined = ''
            item.forEach(el => {
              joined += el
            })

            if (i === 0) {
              out = joined
            } else {
              out = out + ' ' + joined
            }
          })

          if (this.maxLength !== null) {
            return out.substring(0, this.maxLength + 3)
          }
          return out
        }
        if (!this.value) {
          if (this.maxLength !== null && this.localValue !== null) {
            return this.localValue.substring(0, this.maxLength)
          }
          return this.localValue
        }

        if (this.maxLength !== null && this.localValue !== null) {
          return this.value.substring(0, this.maxLength)
        }
        return this.value
      },
      set (val) {
        /**
         * Used for v-model
         * @property {String}
         */
        if (this.typeName === 'tel') {
          this.localValue = val.replace(/\s/g, '')
        } else if (this.typeName === 'card' && typeof this.value === 'string') {
          this.localValue = val.replace(/\s/g, '')
        } else {
          this.localValue = val
        }
        this.$emit('input', this.localValue)
        return val
      }
    },
    inputChanged () {
      let classList = ''
      if (this.inputValue) classList = classList + ' input-group--active'
      if (this.error) classList = classList + ' input-group--error'

      return classList
    },
    iconClass () {
      let modifier

      if (this.iconPosition === 'left') {
        modifier = 'input-group__icon--left'
      }

      if (this.iconPosition === 'right') {
        modifier = 'input-group__icon--right'
      }

      return `${modifier} ${this.icon}`
    }
  }
}
</script>
<style lang="scss">
@mixin inputActive {
  padding: 1rem 1.4rem;
}

@mixin inputActiveWithIconLeft {
  padding-left: 3.5rem;
}

@mixin labelActive {
  opacity: 1;
  visibility: visible;
  font-size: 1.4rem;
  transform: translateY(-1rem);
}

.input-group {
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;

  &__label {
    margin-bottom: 1rem;
    position: absolute;
    top: .9rem;
    left: 1.6rem;
    font-size: 1.4rem;
    color: $color-space;
    opacity: 0;
    visibility: hidden;
    transition: all .2s;
    line-height: .2;
    font-weight: 400;

    background-color: $color-white;

    padding-left: 4px;
    padding-right: 4px;
  }

  &__input {
    appearance: none;
    cursor: text;

    border: 1.3px $color-azure solid;
    border-radius: $border-radius;
    padding: 1rem 1.4rem;
    font-family: inherit;
    font-size: 1.4rem;

    &--error {
      border-color: $color-error;
    }

    &::placeholder {
      color: $color-space;
      opacity: 1;
      transition: all .2s
    }

    &:focus {
      @include inputActive;
      outline: none;

      &.input-group__input::placeholder {
        opacity: 0;
      }
    }

    &:disabled, &--disabled {
      background-color: $color-grey-extra-light;
      cursor: unset!important;
    }

    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }

    &[type='number'] {
      -moz-appearance: textfield;
    }
  }

  &__input:focus + .input-group__label {
    @include labelActive;
  }

  &__icon, &__spinner {
    position: absolute;
    font-size: 1.6rem;
    color: $color-grey-light;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    padding: 1rem;
    transition: all .2s;

    &[type="submit"] {
      cursor: pointer;
    }

    &:focus {
      outline: none;
    }

    &:active {
      transform: translateY(-49.5%);
    }
  }

  &__spinner {
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    padding-top: 1.3rem;
  }

  &--error {
    .input-group__icon {
      top: calc(50% - 1rem)
    }

    .input-group__spinner {
      top: calc(50% - 1rem)
    }
  }

  &--active {
    .input-group__label {
      @include labelActive;

    }

    .input-group__input {
      @include inputActive;

    }

    .input-group__icon--left ~ .input-group__input {
      @include inputActiveWithIconLeft;

    }
  }

  &__icon--right + &__input {
    padding-right: 3rem;
  }

  &__icon--left + &__input {
    padding-left: 3.5rem;

    &:focus {
      @include inputActiveWithIconLeft
    }
  }

  &__icon--left ~ &__label {
    left: 3.6rem;
  }

  &__icon--right {
    right: 0;
  }

  &__icon--left {
    left: 0;
  }

  &__error {
    margin-top: .2rem;
    color: $color-error;
  }
}
</style>
